import { redirect } from '@tanstack/react-router';
import type { RouterContext } from '@/routes/__root';
import { useCampaignStore } from '@/stores';
import type { CapabilityArea } from '@signalvote/shared';
import { CapabilityTier } from '@signalvote/shared';

/**
 * Require authentication for a route.
 * Redirects to /login with the intended destination in search params.
 */
export function requireAuth(context: RouterContext, redirectTo: string) {
  if (!context.auth.user) {
    throw redirect({
      to: '/login',
      search: { redirect: redirectTo },
    });
  }
  return context.auth.user;
}

/**
 * Require specific roles for a route.
 * First checks auth, then checks if user has one of the required roles.
 * Redirects to /dashboard if user doesn't have required role.
 */
export function requireRole(
  context: RouterContext,
  roles: string[],
  redirectTo: string
) {
  const user = requireAuth(context, redirectTo);
  if (roles.includes('admin') || roles.includes('superadmin')) {
    if (!user.isPlatformAdmin) {
      throw redirect({ to: '/app' });
    }
    return user;
  }
  // Fallback for any other role check
  if (!user.isPlatformAdmin) {
    throw redirect({ to: '/app' });
  }
  return user;
}

/**
 * Require a specific capability for a route.
 * Platform admins and campaign owners bypass capability checks.
 * Redirects to /app if user lacks the required capability.
 */
export function requireCapability(
  context: RouterContext,
  area: CapabilityArea,
  minTier: CapabilityTier,
  redirectTo: string
) {
  const user = requireAuth(context, redirectTo);
  if (user.isPlatformAdmin) return user;
  const { hasCapability } = useCampaignStore.getState();
  if (!hasCapability(area, minTier)) {
    throw redirect({ to: '/app' });
  }
  return user;
}

/**
 * Require a campaign to be selected for campaign-scoped routes.
 * Redirects to campaign selection page if no campaign is selected.
 *
 * Use this in beforeLoad for routes that require campaign context:
 * ```ts
 * beforeLoad: ({ context }) => {
 *   requireAuth(context, '/admin/voters');
 *   requireCampaign('/admin/home');
 * }
 * ```
 */
export function requireCampaign(redirectTo: string = '/app/home') {
  const { selectedCampaign } = useCampaignStore.getState();
  if (!selectedCampaign) {
    throw redirect({ to: redirectTo });
  }
  return selectedCampaign;
}
